import { Box, ListSubheader, MenuItem } from '@mui/material'
import { CaretDown } from '@phosphor-icons/react'
import { useEffect, useMemo, useState } from 'react'
import { ClipLoader } from 'react-spinners'
import {
  FormControlStyled,
  InputLabelStyled,
  NormalText,
  SelectStyled,
} from '../styles'
import { Button } from './../button/button'
import { TextFieldStyled } from './../styles'
import { normalizeText } from '../utils'

// Cuantas opciones se dibujan como maximo. Es un tope de render, no de datos:
// el filtro corre sobre la lista completa, asi que escribiendo se llega a
// cualquier contacto aunque no aparezca en la primera tanda.
const MAX_VISIBLE_OPTIONS = 50

export const Dropdown = ({
  label,
  defaultValue,
  options,
  isLoading = false,
  onSearch = () => {},
  withSearch = true,
  onSelect,
  placeholder = 'Search...',
  required = false,
  width = '100%',
  styles = {},
  withBorder = true,
  withLabel = true,
}) => {
  const [searchText, setSearchText] = useState('')
  const [valueSelected, setValueSelected] = useState(defaultValue || '')

  const handleDelete = () => {
    setSearchText('')
    onSearch('')
    onSelect('')
  }

  const handleChange = (option) => {
    setValueSelected(option?.value)
    // Se limpia la busqueda al elegir. Si no, la proxima vez que se abre el
    // desplegable sigue filtrado por lo anterior y parece que falta gente.
    setSearchText('')
    if (onSelect) {
      onSelect(option?.value)
    }
  }

  useEffect(() => {
    setValueSelected(defaultValue)
  }, [defaultValue])

  // Deduplicado + filtrado + tope de opciones dibujadas. Va todo en un solo
  // useMemo porque son tres pasadas sobre la misma lista y con la agenda
  // completa la lista es grande.
  //
  // DEDUPLICADO. El `value` es a la vez la key de React y lo que guarda el
  // Select. Dos contactos de agenda apuntando a la misma wallet daban keys
  // repetidas —React descarta el render de una de las dos— y una seleccion
  // ambigua. Gana el primero. Tambien se descartan las opciones sin value:
  // un contacto sin wallet no es elegible, y antes se podia elegir y dejaba
  // el destino en null.
  //
  // FILTRADO. Sobre las opciones YA cargadas, en el navegador, mientras se
  // escribe. Hasta ahora la caja de busqueda solo disparaba `onSearch` y
  // esperaba que el caller filtrara; ninguno lo hacia, asi que no hacia nada.
  // Se filtra por label y por value, para poder pegar una wallet entera.
  //
  // `onSearch` se sigue llamando (Enter y boton Search) para quien quiera
  // resolverlo contra el backend. En la agenda eso NO es posible: los campos
  // estan cifrados en la base y no hay LIKE sobre ciphertext.
  //
  // TOPE. La pantalla de pago carga la agenda entera (elements=100000). Armar
  // un MenuItem por contacto en cada render congela el navegador mucho antes
  // de que el usuario llegue a scrollear hasta el que busca. Se dibujan los
  // primeros MAX_VISIBLE y al resto se llega escribiendo.
  const { optionsVisible, totalMatches, hasMore } = useMemo(() => {
    const vistos = new Set()
    const unicas = []

    for (const option of options || []) {
      const value = option?.value
      if (value === null || value === undefined || value === '') continue
      if (vistos.has(value)) continue
      vistos.add(value)
      unicas.push(option)
    }

    const needle = withSearch ? normalizeText(searchText.trim()) : ''
    const matches = needle
      ? unicas.filter(
          (option) =>
            normalizeText(option?.label).includes(needle) ||
            normalizeText(option?.value).includes(needle)
        )
      : unicas

    const visible = matches.slice(0, MAX_VISIBLE_OPTIONS)

    // MUI resuelve el texto que muestra el campo buscando entre los hijos el
    // MenuItem cuyo value coincide. Si el seleccionado queda afuera —por el
    // filtro o por el tope— tira "out-of-range value" por consola y el campo
    // se ve vacio aunque haya una wallet elegida. Se lo agrega siempre.
    if (
      valueSelected !== null &&
      valueSelected !== undefined &&
      valueSelected !== '' &&
      !visible.some((option) => option?.value === valueSelected)
    ) {
      const seleccionada = unicas.find(
        (option) => option?.value === valueSelected
      )
      if (seleccionada) visible.unshift(seleccionada)
    }

    return {
      optionsVisible: visible,
      totalMatches: matches.length,
      hasMore: matches.length > MAX_VISIBLE_OPTIONS,
    }
  }, [options, searchText, withSearch, valueSelected])

  return (
    <FormControlStyled
      withBorder={withBorder}
      width={width}
      sx={{
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#F5F5F5',
        },
        ...styles,
      }}
    >
      {withLabel && valueSelected && (
        <NormalText >{label}</NormalText>
      )}
      {!valueSelected && (
        <InputLabelStyled id={`${label}-select-label`} shrink={false}>
          {label} {required && '*'}
        </InputLabelStyled>
      )}
      <SelectStyled
        MenuProps={{ autoFocus: false, disablePortal: true }}
        labelId={`${label}-select-label`}
        id={`${label}-select`}
        value={valueSelected || defaultValue}
        label={label}
        size="small"
        IconComponent={() => (
          <CaretDown
            size={16}
            weight="regular"
            style={{ pointerEvents: 'none' }}
          />
        )}
      >
        {withSearch && (
          <ListSubheader>
            <Box display={'flex'} gap={'8px'}>
              <TextFieldStyled
                autoFocus
                placeholder={placeholder}
                fullWidth
                onChange={(e) => setSearchText(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    onSearch(e.target.value)
                  }
                }}
                // Sin esto el buscador es inusable: el MenuList de MUI se queda
                // con las teclas para su navegacion por texto y despues de la
                // primera letra el foco salta del input a una opcion.
                onKeyDown={(e) => {
                  e.stopPropagation()
                }}
                onClick={(e) => {
                  e.stopPropagation()
                }}
                value={searchText}
              />
              <Box
                display={'flex'}
                gap={'8px'}
                justifyContent={'flex-end'}
                margin={'8px 0px 0px 0px'}
              >
                {/* El portal del cliente esta en ingles. Estos dos botones
                    nunca se habian visto porque ningun caller encendia el
                    buscador; con el selector de contactos ya se ven. */}
                <Button label={'Clear'} onClick={handleDelete} />
                <Button label={'Search'} onClick={() => onSearch(searchText)} />
              </Box>
            </Box>
          </ListSubheader>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <ClipLoader color={'#0197f6'} size={24} />
          </Box>
        ) : optionsVisible?.length > 0 ? (
          // Se devuelve un array, no un Fragment: MUI recorre los hijos con
          // React.Children para encontrar el seleccionado y no ve adentro de
          // un Fragment. Con un array si.
          [
            ...optionsVisible.map((option) => (
              <MenuItem
                key={option?.value}
                value={option?.value}
                onClick={() => handleChange(option)}
                disabled={option?.disabled}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {option.icon && (
                  <img
                    src={option.icon}
                    alt={option.label}
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '100%',
                    }}
                  />
                )}
                {option.label}
              </MenuItem>
            )),
            hasMore ? (
              <Box
                key={'__dropdown_more__'}
                display={'flex'}
                justifyContent={'center'}
                padding={'8px'}
              >
                <NormalText>
                  {`Showing ${MAX_VISIBLE_OPTIONS} of ${totalMatches} — keep typing to narrow it down`}
                </NormalText>
              </Box>
            ) : null,
            // Sin coincidencias la lista no queda vacia: sigue estando el
            // seleccionado, que se fija aparte para que MUI pueda dibujar el
            // texto del campo. Sin este aviso parece que el filtro no hizo
            // nada.
            totalMatches === 0 ? (
              <Box
                key={'__dropdown_empty__'}
                display={'flex'}
                justifyContent={'center'}
                padding={'8px'}
              >
                <NormalText>No matches</NormalText>
              </Box>
            ) : null,
          ]
        ) : (
          <Box display={'flex'} justifyContent={'center'}>
            <NormalText>No matches</NormalText>
          </Box>
        )}
      </SelectStyled>
    </FormControlStyled>
  )
}
